import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, Injector, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
    type ActivatedRoute,
    type ActivatedRouteSnapshot,
    PRIMARY_OUTLET,
    type Resolve,
    Router,
} from '@angular/router';
import { type Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import type { WithCrudService } from '@app/shared/api/crud.service';
import { Helpers } from '@app/shared/common/helpers';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/common/object-service-mapper';
import { OBJECT_INFO, type OBJECTS } from '@app/shared/common/shared.constant';
import { ProjectService } from '@app/shared/project/project.service';
import { RecentActivityService } from '@app/shared/project/recent-activity.service';
import { TAB_ROUTES } from '@app/shared/routing/route-configs';
import { RouterHelper } from '@app/shared/routing/router-helper';

interface Breadcrumb {
    label: string;
    params?: Record<string, string>;
    url?: string;
    icon?: string;
}

@Injectable({
    providedIn: 'root',
})
export class BreadcrumbService implements Resolve<any> {
    static readonly BREADCRUMB_TITLES = {
        DASHBOARD: 'Dashboard',
        RUN_JOB: 'Run Job',
        SNAPSHOT_VIEW: 'Snapshot View',
    } as const;

    private readonly injector = inject(Injector);
    private readonly titleService = inject(Title);
    private readonly recentActivityService = inject(RecentActivityService);
    private readonly router = inject(Router);
    private readonly projectService = inject(ProjectService);

    readonly breadcrumbData = signal<Breadcrumb[]>([]);

    resolve(route: ActivatedRouteSnapshot): Observable<Breadcrumb[]> {
        return this.getBreadcrumbDetails(route);
    }

    generateBreadcrumbData(route: ActivatedRoute): void {
        const breadcrumbs = this.collectBreadcrumbs(route);
        const subtitle = breadcrumbs
            .map(b => b.label)
            .reverse()
            .join(' • ');
        this.titleService.setTitle(subtitle || 'GenGuardX');
        this.breadcrumbData.set(breadcrumbs);
    }

    /** Renames a breadcrumb (and so the page title), e.g. after the object was renamed. */
    updateBreadcrumbLabel(oldLabel: string, newLabel: string): void {
        const breadcrumbs = this.breadcrumbData().map(b =>
            b.label === oldLabel ? { ...b, label: newLabel } : b,
        );
        this.titleService.setTitle(
            breadcrumbs
                .map(b => b.label)
                .reverse()
                .join(' • ') || 'GenGuardX',
        );
        this.breadcrumbData.set(breadcrumbs);
    }

    private collectBreadcrumbs(
        route: ActivatedRoute,
        url: string = '',
        breadcrumbs: Breadcrumb[] = [],
    ): Breadcrumb[] {
        const routeDataBreadcrumb = 'breadcrumb';

        // Base case: no child routes
        if (!route.firstChild) {
            return breadcrumbs;
        }

        const childRoute = route.firstChild;
        const routeURL = childRoute.snapshot.url.map(segment => segment.path).join('/');
        const newURL = url + (routeURL ? `/${routeURL}` : '');

        // Ensure primary outlet and breadcrumb existence
        if (
            childRoute.outlet === PRIMARY_OUTLET &&
            (routeDataBreadcrumb in childRoute.snapshot.data ||
                childRoute.routeConfig?.resolve?.customBreadcrumbs)
        ) {
            const customBreadcrumbs: Breadcrumb[] = childRoute.routeConfig?.resolve
                ?.customBreadcrumbs
                ? childRoute.snapshot.data.customBreadcrumbs || []
                : [
                      {
                          label: childRoute.snapshot.data[routeDataBreadcrumb],
                          params: childRoute.snapshot.params,
                          url: newURL,
                      },
                  ];

            customBreadcrumbs.forEach(breadcrumb => {
                if (!breadcrumbs.some(b => b.label === breadcrumb.label)) {
                    breadcrumbs.push(breadcrumb);
                }
            });

            return this.collectBreadcrumbs(childRoute, newURL, breadcrumbs);
        }

        // Recursive call for the next child
        return this.collectBreadcrumbs(childRoute, newURL, breadcrumbs);
    }

    private getBreadcrumbDetails(routeSnapshot: ActivatedRouteSnapshot): Observable<Breadcrumb[]> {
        const { breadcrumb } = routeSnapshot.data;
        const { id } = routeSnapshot.params;
        const { projectSlug } = routeSnapshot.params;
        const object = routeSnapshot.data.object as OBJECTS;

        if (projectSlug && !object) {
            return this.projectService.getBySlug(projectSlug, false).pipe(
                map(({ result: project }) => [
                    {
                        label: project.name || projectSlug,
                        url: `/${projectSlug}`,
                        icon: 'pi pi-box',
                    },
                ]),
                catchError(err => {
                    if (
                        err instanceof HttpErrorResponse &&
                        err.status === Number(HttpStatusCode.NotFound)
                    ) {
                        this.router.navigate(['/not-found']);
                    }
                    return throwError(() => err);
                }),
            );
        }

        const service = this.injector.get<WithCrudService<{ id?: number; name: string }>>(
            OBJECT_SERVICE_MAPPER[object],
        );
        const displayName = OBJECT_INFO[object].name;

        const fetchData$: Observable<{
            result: {
                id?: number;
                name: string;
                projectSlug?: string;
                previewInfo?: {
                    createdDate: string;
                    versionName: string;
                };
            };
        }> = id
            ? // FIXME: basicInfo=true doesn't do anything in preview as we don't support it for snapshots yet
              service
                  .getById(
                      id,
                      { transactionId: routeSnapshot.params.transactionId, basicInfo: true },
                      { showError: false }, // If the ID doesn't exist - don't show an error
                  )
                  .pipe(
                      catchError(err => {
                          if (
                              err instanceof HttpErrorResponse &&
                              err.status === Number(HttpStatusCode.NotFound)
                          ) {
                              return of({ result: { name: 'Not Found' } });
                          }
                          return throwError(() => err);
                      }),
                  )
            : of({ result: { name: `New ${displayName}` } });

        return fetchData$.pipe(
            map(response => {
                // Store the recent items details in the browser storage
                if (response.result.id) {
                    const activity = {
                        objectType: object,
                        timestamp: new Date().toISOString(),
                        details: response.result,
                    };

                    // FIXME: We are assuming the getById() API will give us the same properties as GlobalSearchItem
                    this.recentActivityService.addActivity(activity as any);
                }
                /**
                 *  Details:
                        case 1 create: ModuleName(link) > Object Type(link) > New <new object type>(muted text)
                        case 2 edit: ModuleName(link) > Object Type(link) > object name(muted text)
                    Simulation:
                        case 1: Run Simulation: ModuleName(link) > Object Type(link) > object name(link) > Run <Job-Type muted text>
                        case 2. Simulation Details: ModuleName(link) > Object Type(link) > object name(link) > Dashboard
                    Singleton Objects:
                        only case edit: ModuleName(link) > Object Type(link)
                 */
                const breadcrumbs: Breadcrumb[] = [];

                const detailsPageUrl = RouterHelper.buildRoute({
                    module: routeSnapshot.data.moduleName,
                    object,
                    id,
                    projectSlug: response.result.projectSlug,
                });

                if (response.result.name) {
                    breadcrumbs.push({ label: response.result.name, url: detailsPageUrl });
                }
                if (
                    [
                        BreadcrumbService.BREADCRUMB_TITLES.RUN_JOB,
                        BreadcrumbService.BREADCRUMB_TITLES.DASHBOARD,
                    ].includes(breadcrumb)
                ) {
                    // Add a breadcrumb link to job history page by default
                    breadcrumbs.push({
                        label: 'Jobs',
                        url: `${detailsPageUrl}/${TAB_ROUTES.JOBS}`,
                    });

                    if (breadcrumb === BreadcrumbService.BREADCRUMB_TITLES.DASHBOARD) {
                        breadcrumbs.push({ label: breadcrumb });
                    }

                    if (breadcrumb === BreadcrumbService.BREADCRUMB_TITLES.RUN_JOB) {
                        breadcrumbs.push({ label: breadcrumb });
                    }
                }

                if (
                    breadcrumb === BreadcrumbService.BREADCRUMB_TITLES.SNAPSHOT_VIEW &&
                    response.result.previewInfo
                ) {
                    breadcrumbs.push({
                        label: response.result.previewInfo?.versionName
                            ? response.result.previewInfo.versionName
                            : response.result.previewInfo?.createdDate
                              ? Helpers.getPlaceholderName(
                                    'Snapshot at',
                                    response.result.previewInfo.createdDate,
                                )
                              : 'Snapshot',
                    });
                }

                return breadcrumbs;
            }),
        );
    }
}
